import {
  CorePortErrors,
  CubePortImplementation,
  CubeReducerState,
  CubeStatus,
  ScheduleCubePorts
} from 'modules/Cube/domain/cube.domain.types'
import { useLoadBillingScheduleEditor } from 'modules/Cube/communication/external/billingSchedule.api.v1/ports/useLoadBillingScheduleEditor'
import { useNotifications } from '@sequencehq/core-components'
import { useSaveBillingScheduleEditor } from 'modules/Cube/communication/external/billingSchedule.api.v1/ports/useSaveBillingScheduleEditor'
import { billingScheduleAdapter } from 'modules/Cube/communication/external/billingSchedule.api.v1/adapters/billingSchedule.adapters'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CubeDomainInterface } from 'modules/Cube/domain/cube.domain'
import deepmerge from 'deepmerge'
import { useLoadSingleCustomer } from 'modules/Cube/communication/external/common.api.v1/ports/entityLoaders'
import invariant from 'tiny-invariant'
import { useLoadSchedulePreview } from 'modules/Cube/communication/external/billingSchedule.api.v1/ports/entityLoaders'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { logError } from '@sequencehq/utils'
import { useMutation } from '@sequencehq/api'
import { getApiErrorMessage } from 'modules/Cube/utils/getApiErrorMessage'

type UseBillingSchedulePorts = (props: {
  scheduleId?: string
}) => CubePortImplementation

export const useBillingSchedulePortImplementation: UseBillingSchedulePorts =
  props => {
    const { displayNotification, displayPromiseNotification } =
      useNotifications()
    const navigate = useNavigate()
    const [existingData, setExistingData] =
      useState<CubeDomainInterface['queries']['rawData']['data']>()

    const billingScheduleLoader = useLoadBillingScheduleEditor()
    const saveBillingScheduleEditor = useSaveBillingScheduleEditor()
    const customerLoader = useLoadSingleCustomer()
    const schedulePreviewLoader = useLoadSchedulePreview()

    const transformSaveData = useCallback(
      (domainOutput: CubeDomainInterface['queries']) => {
        try {
          if (!existingData) {
            throw new Error("Can't save without initialising first")
          }
          return billingScheduleAdapter.out(existingData)(domainOutput)
        } catch (e) {
          logError(e)
          displayNotification('Failed to save draft', {
            type: 'error'
          })
          console.error(e)
          return
        }
      },
      [existingData, displayNotification]
    )

    const saveBillingSchedule = useCallback(
      async (
        domainOutput: CubeDomainInterface['queries']
      ): Promise<{
        success: boolean
        id?: string
      }> => {
        if (!existingData) {
          throw new Error("Can't save without initialising first")
        }

        const dataToSave = transformSaveData(domainOutput)

        invariant(dataToSave, 'Could not create save data')
        console.info(`%cSaving data...`, 'color: blue', { dataToSave })

        if (
          props.scheduleId &&
          [CubeStatus.ScheduleActive, CubeStatus.SchedulePending].includes(
            existingData.common.status
          )
        ) {
          const updatePromise = saveBillingScheduleEditor.updateBillingSchedule(
            props.scheduleId
          )(dataToSave)

          try {
            const saveResult = await displayPromiseNotification(updatePromise, {
              loading: 'Updating schedule...',
              success: 'Schedule updated',
              error: (e: unknown) =>
                getApiErrorMessage(e, 'Failed to update schedule')
            })

            return {
              success: saveResult.success,
              id: saveResult.schedule?.id
            }
          } catch (error) {
            return {
              success: false
            }
          }
        }

        const startPromise = saveBillingScheduleEditor.activateSchedule(
          props.scheduleId
        )(dataToSave)

        try {
          const saveResult = await displayPromiseNotification(startPromise, {
            loading: 'Starting schedule...',
            success: 'Schedule started',
            error: (e: unknown) =>
              getApiErrorMessage(e, 'Failed to start schedule')
          })

          if (saveResult.success && saveResult.schedule?.id) {
            /**
             * Change the URL to reflect the new schedule ID.
             */
            navigate(`/billing-schedules/${saveResult.schedule.id}/edit`)
            return {
              success: true,
              id: saveResult.schedule.id
            }
          }

          return {
            success: false
          }
        } catch (error) {
          return {
            success: false
          }
        }
      },
      [
        saveBillingScheduleEditor,
        props.scheduleId,
        existingData,
        navigate,
        displayPromiseNotification,
        transformSaveData
      ]
    )

    const saveScheduleDraft = useCallback(
      async (
        domainOutput: CubeDomainInterface['queries']
      ): Promise<{
        success: boolean
        id?: string
      }> => {
        if (!existingData) {
          throw new Error("Can't save draft without initialising first")
        }

        const dataToSave = transformSaveData(domainOutput)

        invariant(dataToSave, 'Could not create save data')

        console.info(
          `%cSaving draft...`,
          'color: cyan; backgroundColor: blue;',
          {
            dataToSave
          }
        )

        if (!props.scheduleId) {
          const createPromise =
            saveBillingScheduleEditor.createBillingScheduleDraft(dataToSave)

          try {
            const saveResult = await displayPromiseNotification(createPromise, {
              loading: 'Creating draft...',
              success: () => 'Draft created',
              error: (e: unknown) =>
                getApiErrorMessage(e, 'Failed to create draft')
            })

            if (saveResult.newSchedule?.id) {
              navigate(`/billing-schedules/${saveResult.newSchedule?.id}/edit`)
            }

            return {
              success: saveResult.success,
              id: saveResult.newSchedule?.id
            }
          } catch (error) {
            return {
              success: false
            }
          }
        }

        const updatePromise = saveBillingScheduleEditor.updateBillingSchedule(
          props.scheduleId
        )(dataToSave)

        try {
          const saveResult = await displayPromiseNotification(updatePromise, {
            loading: 'Updating draft...',
            success: 'Draft saved',
            error: (e: unknown) =>
              getApiErrorMessage(e, 'Failed to update draft')
          })

          return {
            success: saveResult.success,
            id: saveResult.schedule?.id
          }
        } catch (error) {
          return {
            success: false
          }
        }
      },
      [
        saveBillingScheduleEditor,
        props.scheduleId,
        existingData,
        navigate,
        displayPromiseNotification,
        transformSaveData
      ]
    )

    const saveSchedule = useCallback(
      async (
        domainOutput: CubeDomainInterface['queries']
      ): Promise<{
        success: boolean
        id?: string
      }> => {
        if (!existingData) {
          throw new Error("Can't save without initialising first")
        }

        try {
          const res = await saveBillingSchedule(domainOutput)
          return res
        } catch (e) {
          displayNotification('An error occurred saving the schedule', {
            type: 'error'
          })
          return {
            success: false
          }
        }
      },
      [saveBillingSchedule, existingData, displayNotification]
    )

    const loadCore = useCallback(
      (ctx: { configuration: CubeReducerState['configuration'] }) =>
        async () => {
          const { data: billingScheduleData, error } =
            await billingScheduleLoader(ctx)(props.scheduleId)

          if (!billingScheduleData || error) {
            return {
              data: null,
              error: error || CorePortErrors.Other
            }
          }

          const transformedData = billingScheduleAdapter.in(
            billingScheduleData,
            deepmerge(ctx.configuration, {
              features: {
                stripeIntegrationAvailable: Boolean(
                  billingScheduleData.integrations.find(
                    integration =>
                      integration.service === 'Stripe' && integration.enabled
                  )
                ),
                goCardlessIntegrationAvailable: Boolean(
                  billingScheduleData.integrations.find(
                    integration =>
                      integration.service === 'GoCardless' &&
                      integration.enabled
                  )
                ),
                contractTagEditing:
                  ctx.configuration.features.contractTagEditing &&
                  Boolean(
                    billingScheduleData.integrations.find(
                      integration =>
                        integration.service === 'NetSuite' &&
                        integration.enabled
                    )
                  )
              }
            })
          )

          setExistingData(transformedData.data)

          return {
            data: transformedData,
            error: null
          }
        },
      [billingScheduleLoader, props.scheduleId]
    )

    const archiveScheduleMutation = useMutation(
      dashboard20240730Client.postBillingScheduleArchive
    )

    const archiveSchedule = useCallback(async (): Promise<boolean> => {
      if (!existingData) {
        throw new Error("Can't archive without initialising first")
      }

      const response = await archiveScheduleMutation.mutateAsync({
        id: existingData.common.id
      })

      if (!response) {
        displayNotification('Could not archive schedule', {
          type: 'error'
        })
        return false
      }

      displayNotification('Schedule archived', {
        type: 'success'
      })
      return true
    }, [archiveScheduleMutation, existingData, displayNotification])

    const portImplementation: CubePortImplementation = useCallback(
      (ctx): ScheduleCubePorts => {
        return {
          in: {
            core: loadCore(ctx),
            customer: customerLoader,
            schedule: {
              preview: schedulePreviewLoader
            }
          },
          out: {
            save: saveScheduleDraft,
            archive: archiveSchedule,
            schedule: {
              activate: saveSchedule
            }
          }
        }
      },
      [
        loadCore,
        saveScheduleDraft,
        schedulePreviewLoader,
        saveSchedule,
        customerLoader,
        archiveSchedule
      ]
    )

    return portImplementation
  }
